if (!customElements.get('pdp-option-modals')) {
  // Every element the component treats as an option source. Kept in step with
  // #getSourceSelector and with updateOptionsPocSources in assets/liquid-pdp.js,
  // which is what replaces them on a section fetch.
  const SOURCE_SELECTOR = [
    '.liquid-pdp__variant-selector-wrapper',
    'variant-selects',
    '.js-your-cut-selector',
    'product-style',
    'product-pack-selector',
    'product-colors',
    't-custom-pack',
  ].join(', ')

  // Only the first instance on the page rides the shared slide-up reveal. Any
  // instance connected after that comes from a section re-render, and replaying
  // the fade there flashed the whole option block back to zero opacity.
  let hasFirstInstanceConnected = false
  // Safety net window for the shared reveal pass to run before we show anyway.
  const REVEAL_FALLBACK_MS = 2000

  class PdpOptionModals extends HTMLElement {
    #observer
    #loadingObserver
    #loadingRequestStarted = false
    #loadingStartTimer
    #loadingMaxTimer
    #pendingRebuild = false
    #renderedSignature
    #rebuildFrame
    #activeTrigger
    #colorDraft
    #openPanelKey
    #hasBuilt = false
    #revealTimer

    connectedCallback() {
      this.#setupReveal()
      this.productInfo = this.closest('product-info')
      this.cards = this.querySelector('[data-option-cards]')
      if (!this.productInfo || !this.cards) return

      this.addEventListener('click', this.#handleClick)
      this.addEventListener('change', this.#handleChange)
      document.addEventListener('sizeChanged', this.#handleSizeChanged)

      this.#observer = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => this.#isSourceMutation(mutation))) {
          this.#scheduleRebuild()
        }
      })
      this.#observer.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ['checked', 'data-color-list', 'data-bundle-mode'],
        childList: true,
        subtree: true,
      })
      this.#loadingObserver = new MutationObserver((mutations) => {
        const formChanged = mutations.some(
          (mutation) =>
            mutation.type === 'childList' || mutation.target.matches?.('product-form'),
        )
        if (formChanged) this.#syncLoadingState()
      })

      this.#rebuild()
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#handleClick)
      this.removeEventListener('change', this.#handleChange)
      document.removeEventListener('sizeChanged', this.#handleSizeChanged)
      this.#setLoadingState(false)
      this.#observer?.disconnect()
      this.#loadingObserver?.disconnect()
      if (this.#loadingStartTimer) window.clearTimeout(this.#loadingStartTimer)
      if (this.#loadingMaxTimer) window.clearTimeout(this.#loadingMaxTimer)
      if (this.#revealTimer) window.clearTimeout(this.#revealTimer)
      if (this.#rebuildFrame) cancelAnimationFrame(this.#rebuildFrame)
    }

    // Hands the first render to animations.js so it fades in on its turn in the
    // PDP stagger, and shows every later one outright.
    #setupReveal() {
      if (!this.classList.contains('anim-slide-up')) return
      // animations.js is a deferred head script, so it has already run by the
      // time this element upgrades - a missing helper means it never loaded and
      // nothing else would ever take the block off zero opacity.
      if (hasFirstInstanceConnected || !window.setupScrollAnimations) {
        this.#reveal()
        return
      }

      hasFirstInstanceConnected = true
      this.#revealTimer = window.setTimeout(() => this.#reveal(), REVEAL_FALLBACK_MS)
    }

    #reveal() {
      this.setAttribute('data-animate-init', '')
      this.classList.add('is-visible')
    }

    #startLoadingState() {
      this.#loadingRequestStarted = false
      this.#setLoadingState(true)
      this.#loadingObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      })
      if (this.#loadingStartTimer) window.clearTimeout(this.#loadingStartTimer)
      this.#loadingStartTimer = window.setTimeout(() => {
        this.#loadingStartTimer = undefined
        if (!this.#loadingRequestStarted) this.#setLoadingState(false)
      }, 100)

      // Hard cap. The loading state suppresses pointer events on the whole product
      // block and now defers rebuilds too, so a request that never reports back
      // must not be able to strand it.
      if (this.#loadingMaxTimer) window.clearTimeout(this.#loadingMaxTimer)
      this.#loadingMaxTimer = window.setTimeout(() => {
        this.#loadingMaxTimer = undefined
        this.#setLoadingState(false)
      }, 5000)
    }

    #syncLoadingState() {
      const productForm = this.productInfo.querySelector('product-form')
      if (productForm?.classList.contains('loading')) {
        this.#loadingRequestStarted = true
        return
      }
      if (this.#loadingRequestStarted) {
        this.#setLoadingState(false)
        this.#scheduleRebuild()
      }
    }

    #setLoadingState(isLoading) {
      this.productInfo.toggleAttribute('data-pdp-options-loading', isLoading)
      if (isLoading) this.productInfo.setAttribute('aria-busy', 'true')
      else this.productInfo.removeAttribute('aria-busy')
      if (!isLoading) {
        this.#loadingRequestStarted = false
        if (this.#loadingStartTimer) window.clearTimeout(this.#loadingStartTimer)
        this.#loadingStartTimer = undefined
        if (this.#loadingMaxTimer) window.clearTimeout(this.#loadingMaxTimer)
        this.#loadingMaxTimer = undefined
        this.#loadingObserver?.disconnect()
        // The attribute is already off above, so this cannot re-defer. Covers both
        // exits from the loading state: the fetch completing and the 100ms timeout
        // for a change that never started one.
        if (this.#pendingRebuild) {
          this.#pendingRebuild = false
          this.#scheduleRebuild()
        }
      }
    }

    #scheduleRebuild() {
      if (this.#isOriginalBundleModalOpen()) return
      // While the loading state is up the sources are mid-swap, so a rebuild paints
      // a card with empty or half-replaced content that the next pass immediately
      // throws away - the flicker on a pack change. #setLoadingState flushes the
      // deferred rebuild once the fetch has landed.
      if (this.productInfo.hasAttribute('data-pdp-options-loading')) {
        this.#pendingRebuild = true
        return
      }
      if (this.productInfo.querySelector('product-form')?.classList.contains('loading')) {
        this.#loadingRequestStarted = true
        this.#pendingRebuild = true
        return
      }
      const openDialog = this.querySelector('dialog[open]')
      if (openDialog) {
        openDialog.addEventListener('close', () => this.#scheduleRebuild(), { once: true })
        return
      }
      if (this.#rebuildFrame) cancelAnimationFrame(this.#rebuildFrame)
      this.#rebuildFrame = requestAnimationFrame(() => this.#rebuild())
    }

    #rebuild() {
      this.#restoreSelectedVariants()
      const groups = this.#getGroups()
      if (!groups.length) return

      // A single pack change ends up scheduling several rebuilds - the change
      // handler, the source swap, and the loading state all ask for one, and they
      // land in different frames so the requestAnimationFrame coalescing cannot
      // merge them. Tearing the cards down and rebuilding them identically is what
      // the shopper sees as flicker, so if the output would be the same, don't.
      const signature = this.#renderSignature(groups)
      if (this.dataset.ready === 'true' && signature === this.#renderedSignature) {
        this.#rebuildFrame = undefined
        return
      }
      this.#renderedSignature = signature
      // #openPanelKey is deliberately NOT re-read from the DOM here. A pack change
      // swaps the product, so the cards are torn down and rebuilt several times
      // while the fetch lands; inferring intent from whatever happened to be in the
      // DOM at that moment is what made the card close itself on some passes and
      // survive on others. It now only changes when the shopper acts on a summary.
      const focusedGroupName = this.contains(document.activeElement)
        ? document.activeElement.dataset.optionGroupName
        : ''

      this.productInfo.querySelectorAll('[data-pdp-options-source]').forEach((source) => {
        source.removeAttribute('data-pdp-options-source')
      })
      for (const group of groups) group.hideTarget.dataset.pdpOptionsSource = ''

      this.cards.replaceChildren()
      this.querySelectorAll('.pdp-option-modals__dialog').forEach((dialog) => dialog.remove())

      const packGroup = groups.find((group) => group.type === 'pack')
      const colorsGroup = groups.find((group) => group.type === 'colors')
      for (const [index, group] of groups.entries()) {
        if (group.type === 'pack' || group.type === 'colors') continue
        this.#renderInlineGroup(group, index)
      }
      if (packGroup || colorsGroup) this.#renderPackPanel(packGroup, colorsGroup, groups.length)
      this.dataset.ready = 'true'
      this.#hasBuilt = true
      this.#rebuildFrame = undefined

      if (focusedGroupName) {
        const replacement = Array.from(
          this.querySelectorAll('[data-option-group-name]'),
        ).find((element) => element.dataset.optionGroupName === focusedGroupName)
        replacement?.focus()
      }
    }

    // The observer has to watch the whole product block, because a source is
    // replaced wholesale by its parent rather than mutated in place. But that block
    // also holds the gallery and the price widgets, which churn constantly - the
    // gallery carousel alone fired enough childList mutations to trigger five full
    // rebuilds after a single pack change. Only changes that actually touch an
    // option source count.
    // Everything that decides what the cards look like. Deliberately includes the
    // source markup length so a structural change still forces a rebuild even if
    // the selections happen to match.
    #renderSignature(groups) {
      const parts = groups.map((group) => {
        const inputs = Array.from(group.source.querySelectorAll('input[type="radio"]'))
        const checked = inputs.filter((input) => input.checked)
        return [
          group.type,
          group.label,
          group.value,
          inputs.length,
          group.source.innerHTML.length,
          checked.map((input) => input.value).join('|'),
          checked.map((input) => `${input.dataset.colorList || ''}/${input.dataset.colorPack || ''}`).join('|'),
          inputs.map((input) => (this.#isColorAvailable({ input }) ? '0' : '1')).join(''),
        ].join('~')
      })
      parts.push(
        this.productInfo.dataset.productPackSize || '',
        this.productInfo.dataset.bundleMode || '',
        this.productInfo.dataset.selectedSize || '',
        this.#openPanelKey || '',
        this.#colorDraft ? this.#colorDraft.mode : '',
      )
      return parts.join('||')
    }

    #isSourceMutation(mutation) {
      if (this.contains(mutation.target)) return false
      // Already narrowed to the option-related attributes by attributeFilter.
      if (mutation.type !== 'childList') return true

      const touchesSource = (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.(SOURCE_SELECTOR) || node.querySelector?.(SOURCE_SELECTOR))
      return (
        Array.from(mutation.addedNodes).some(touchesSource) ||
        Array.from(mutation.removedNodes).some(touchesSource)
      )
    }

    #isOriginalBundleModalOpen() {
      return Boolean(
        this.productInfo.querySelector('t-custom-pack [data-fs-overlay][data-open="true"]'),
      )
    }

    #restoreSelectedVariants() {
      const selectedValues = this.productInfo.dataset.selectedSize?.split(' / ').filter(Boolean)
      if (!selectedValues?.length) return

      const variantWrapper = this.productInfo.querySelector('.liquid-pdp__variant-selector-wrapper')
      const variantSelects = Array.from(
        variantWrapper?.querySelectorAll(':scope > variant-selects') || [],
      ).find((source) => source.style.display !== 'none')
      if (!variantSelects) return

      const optionGroups = variantSelects.querySelectorAll(
        ':scope > .liquid-pdp__variant-selector-buttons',
      )
      for (const [index, optionGroup] of Array.from(optionGroups).entries()) {
        if (optionGroup.querySelector('input[type="radio"]:checked')) continue
        const matchingInput = Array.from(
          optionGroup.querySelectorAll('input[type="radio"]'),
        ).find(
          (input) =>
            input.value === selectedValues[index] &&
            !input.disabled &&
            input.getAttribute('aria-disabled') !== 'true',
        )
        if (!matchingInput) continue
        matchingInput.checked = true
        matchingInput.setAttribute('checked', '')
      }
    }

    #getGroups() {
      const groups = []
      const variantWrapper = this.productInfo.querySelector('.liquid-pdp__variant-selector-wrapper')
      const activeVariantSelects = variantWrapper?.querySelector('variant-selects')

      activeVariantSelects
        ?.querySelectorAll(':scope > .liquid-pdp__variant-selector-buttons')
        .forEach((source) => groups.push(this.#createGroup(source, variantWrapper, 'variant')))

      const cutWrapper = this.productInfo.querySelector('.js-your-cut-selector')
      cutWrapper?.querySelectorAll('product-style').forEach((source) => {
        if (source.querySelector('input[type="radio"]')) {
          groups.push(this.#createGroup(source, cutWrapper, 'style'))
        }
      })

      const packSelector = this.productInfo.querySelector('product-pack-selector')
      if (packSelector?.querySelector('input[type="radio"]')) {
        groups.push(this.#createGroup(packSelector, packSelector, 'pack'))
      }

      const colors = this.productInfo.querySelector('product-colors')
      if (colors?.querySelector('input[type="radio"]')) {
        groups.push(this.#createGroup(colors, colors, 'colors'))
      }

      return groups.filter((group) => group.label && group.source.querySelector('input[type="radio"]'))
    }

    #createGroup(source, hideTarget, type) {
      return {
        source,
        hideTarget,
        type,
        label: this.#getLabel(source, type),
        value: this.#getValue(source, type),
      }
    }

    #getLabel(source, type) {
      if (type === 'variant') return source.dataset.optionName || ''
      if (type === 'colors') {
        return source.querySelector('.liquid-pdp__product-colors-heading > span')?.textContent.replace(':', '').trim() || ''
      }

      const heading = source.querySelector(
        '.liquid-pdp__style-toggle-label, .liquid-pdp__pack-selector-wrapper > p',
      )
      const label =
        type === 'style' ? heading?.childNodes[0]?.textContent : heading?.querySelector('span')?.textContent
      return label?.replace(':', '').trim() || ''
    }

    #getValue(source, type) {
      if (type === 'pack') return this.productInfo.dataset.productPackSize || ''

      if (type === 'colors') {
        const selections = this.productInfo.bundleSelections
        if (this.productInfo.dataset.bundleMode === 'true' && Array.isArray(selections)) {
          const colors = selections.map((selection) => selection?.color).filter(Boolean)
          const colorSummary = this.#getColorSummary(colors.join(','))
          if (colorSummary) return `${this.dataset.customPackLabel} (${colorSummary})`
        }
        const display = source.querySelector('[data-color-display]')
        const checked = source.querySelector('input[type="radio"]:checked')
        const colorSummary = this.#getColorSummary(
          checked?.dataset.colorList,
          checked?.dataset.colorPack,
          display?.dataset.colorList,
          display?.textContent,
        )
        const badge = checked
          ?.closest('.liquid-pdp__product-colors-button-wrapper')
          ?.querySelector('.badge')
          ?.textContent.replace(/^[^a-zA-Z0-9]+/, '')
          .trim()
        if (badge && colorSummary) return `${badge} (${colorSummary})`
        if (colorSummary) return colorSummary
      }

      const selectedDisplay = source.querySelector(
        '.liquid-pdp__pack-selector-current-value, .liquid-pdp__style-toggle-label strong',
      )
      if (selectedDisplay?.textContent.trim()) return selectedDisplay.textContent.trim()

      const checked = source.querySelector('input[type="radio"]:checked')
      if (!checked) return ''
      return checked.closest('label')?.textContent.replace(/\s+/g, ' ').trim() || checked.value
    }

    #getColorSummary(...colorLists) {
      const colors = this.#getColors(...colorLists)
      if (!colors.length) return ''
      const uniqueColors = new Set(colors.map((color) => color.toLowerCase()))
      if (colors.length > 1 && uniqueColors.size === 1) return `All ${colors[0]}`
      const visibleColors = colors.slice(0, 3)
      const remainingCount = colors.length - visibleColors.length
      const remainingLabel = remainingCount > 0 ? ` +${remainingCount}` : ''
      return `${visibleColors.join(', ')}${remainingLabel}`
    }

    #getColors(...colorLists) {
      let colors = []
      for (const colorList of colorLists) {
        if (!colorList) continue
        const candidateColors = colorList
          .split(',')
          .map((color) => color.trim())
          .filter(Boolean)
        if (candidateColors.length > colors.length) colors = candidateColors
      }
      return colors
    }

    #getPackColorPresentation(colors) {
      const colorCounts = new Map()
      for (const color of colors) {
        colorCounts.set(color, (colorCounts.get(color) || 0) + 1)
      }

      const uniqueColors = Array.from(colorCounts.keys())
      const formattedColors = Array.from(colorCounts.entries()).map(([color, count]) =>
        count > 1 ? `${count}x ${color}` : color,
      )
      const canExpand = uniqueColors.length > 2

      return {
        shortList: canExpand ? uniqueColors.slice(0, 2).join(', ') : formattedColors.join(', '),
        fullList: formattedColors.join(', '),
        remainingCount: canExpand ? uniqueColors.length - 2 : 0,
      }
    }

    #renderInlineGroup(group, index) {
      const hasSelection = Boolean(group.source.querySelector('input[type="radio"]:checked'))
      const panelKey = `${group.type}-${group.label}-${index}`
      // The unselected-option auto-open is a first-paint affordance only. Selecting
      // an option clears #openPanelKey, so leaving it armed on later rebuilds made
      // an unrelated panel spring open right after a size change - and a source
      // mid-swap can read as unselected even when it is not.
      const shouldOpen =
        this.#openPanelKey === panelKey ||
        (!this.#hasBuilt && !hasSelection && !this.#openPanelKey)
      if (shouldOpen) this.#openPanelKey = panelKey
      const panel = this.#createPanel({
        key: panelKey,
        label: group.label,
        value: group.value,
        groupName: group.source.querySelector('input[type="radio"]')?.name || group.label,
        open: shouldOpen,
      })
      panel.classList.add(`pdp-option-modals__panel--${group.type}`)

      const content = panel.querySelector('[data-option-panel-content]')
      const clonedContent = this.#cloneSource(group, index)
      if (group.type === 'style') this.#prepareStyleClone(clonedContent)
      content.append(clonedContent)

      if (group.type === 'variant') this.#appendSizeHelp(content, group)
      this.cards.append(panel)
    }

    #createPanel({ key, label, value, groupName, open = false, image = '' }) {
      const panel = document.createElement('details')
      panel.className = 'pdp-option-modals__panel'
      panel.dataset.optionPanel = ''
      panel.dataset.panelKey = key
      panel.open = open

      const summary = document.createElement('summary')
      summary.className = 'pdp-option-modals__summary'
      summary.dataset.optionGroupName = groupName
      summary.innerHTML = `
        <span class="pdp-option-modals__summary-main">
          <span class="pdp-option-modals__summary-copy">
            <span class="pdp-option-modals__panel-label"></span>
            <strong class="pdp-option-modals__panel-value"></strong>
          </span>
        </span>
        <span class="pdp-option-modals__panel-action"></span>
      `
      summary.querySelector('.pdp-option-modals__panel-label').textContent = label
      summary.querySelector('.pdp-option-modals__panel-value').textContent = value
      summary.querySelector('.pdp-option-modals__panel-action').textContent = open
        ? this.dataset.doneLabel
        : this.dataset.editLabel

      if (image) {
        const media = document.createElement('span')
        media.className = 'pdp-option-modals__pack-media'
        media.append(this.#createTileImage(image, 62, 74))
        summary.querySelector('.pdp-option-modals__summary-main').prepend(media)
      }

      const content = document.createElement('div')
      content.className = 'pdp-option-modals__panel-content'
      content.dataset.optionPanelContent = ''
      panel.append(summary, content)
      panel.addEventListener('toggle', () => this.#handlePanelToggle(panel))
      return panel
    }

    // Cosmetic only. `toggle` is async and also fires for panels being torn down by
    // a rebuild, so letting it write #openPanelKey let a stale event from a removed
    // card clear the intent for the card that replaced it.
    #handlePanelToggle(panel) {
      // `toggle` is queued, not synchronous. #createPanel opens the card before it
      // is appended, so each rebuild queues a toggle for a panel that the NEXT
      // rebuild has already thrown away. Running the accordion below from that
      // stale handler is what closed the pack card: `panel` was the discarded copy,
      // so the live card looked like a sibling that needed collapsing.
      if (!this.contains(panel)) return

      const action = panel.querySelector('.pdp-option-modals__panel-action')
      if (action) action.textContent = panel.open ? this.dataset.doneLabel : this.dataset.editLabel
      if (!panel.open) {
        panel.classList.remove('pdp-option-modals__panel--opening')
        return
      }

      // Safety net for a panel opened without a click; a click has already closed
      // the siblings synchronously by the time this runs.
      for (const otherPanel of this.querySelectorAll('[data-option-panel][open]')) {
        if (otherPanel === panel) continue
        otherPanel.open = false
      }
    }

    // A freshly inserted <img> paints as an empty box until it decodes, so tiles
    // that were on screen a moment before a rebuild blank to grey and fill back in.
    // These are all above the fold and their bytes are already cached, so decode
    // them before paint instead.
    #createTileImage(src, width, height, className = '') {
      const image = document.createElement('img')
      image.src = src
      image.alt = ''
      image.width = width
      image.height = height
      image.loading = 'eager'
      image.decoding = 'sync'
      if (className) image.className = className
      return image
    }

    // Runs the content fade once, on a real open interaction. Kept off [open] so a
    // rebuild that recreates an already-expanded panel does not replay it.
    #markPanelOpening(panel) {
      panel.classList.add('pdp-option-modals__panel--opening')
      panel.addEventListener(
        'animationend',
        () => panel.classList.remove('pdp-option-modals__panel--opening'),
        { once: true },
      )
    }

    // The pack card groups controls meant to be adjusted together - pack size and
    // the colors in it - so changing one must not collapse the card out from under
    // the shopper. Only the summary's Done closes it.
    #isPackPanel(panel) {
      return Boolean(panel?.classList.contains('pdp-option-modals__panel--pack'))
    }

    // clearIntent distinguishes "collapse this card" from "the shopper is done with
    // this card". Only the latter may drop #openPanelKey, otherwise the next
    // rebuild would helpfully reopen a card the shopper had just finished with.
    #closePanel(panel, { focusSummary = false, clearIntent = true } = {}) {
      if (!panel) return
      panel.open = false
      if (clearIntent && this.#openPanelKey === panel.dataset.panelKey) {
        this.#openPanelKey = undefined
      }
      if (focusSummary) panel.querySelector('summary')?.focus()
    }

    #prepareStyleClone(clone) {
      clone.querySelectorAll(
        '.liquid-pdp__length-toggle-heading, .liquid-pdp__family-toggle-heading',
      ).forEach((heading) => heading.remove())

      if (!clone.querySelector('.liquid-pdp__length-toggle')) return
      const notes = {
        short: this.dataset.lengthShortNote,
        regular: this.dataset.lengthRegularNote,
        tall: this.dataset.lengthTallNote,
      }
      for (const option of clone.querySelectorAll('.liquid-pdp__length-toggle-option')) {
        // data-value carries the length name; the radio's value is the alternate
        // product handle, so it cannot be matched against the note keys.
        const value = option.dataset.value?.toLowerCase()
        if (!notes[value]) continue
        const note = document.createElement('span')
        note.className = 'pdp-option-modals__option-note'
        note.textContent = notes[value]
        option.append(note)
      }
    }

    #appendSizeHelp(content, group) {
      const help = document.createElement('div')
      help.className = 'pdp-option-modals__size-help'
      const tip = document.createElement('span')
      tip.textContent = this.dataset.sizeTip
      help.append(tip)

      const sizeGuide = group.hideTarget.querySelector('open-size-chart')
      if (sizeGuide) {
        const guideButton = document.createElement('button')
        guideButton.type = 'button'
        guideButton.className = 'pdp-option-modals__size-guide'
        guideButton.dataset.sizeGuideProxy = ''
        guideButton.textContent = sizeGuide.textContent.trim()
        help.append(guideButton)
      }
      content.append(help)
    }

    #renderPackPanel(packGroup, colorsGroup, index) {
      const isSingles = this.#isSinglesPack(packGroup)
      const colorData = colorsGroup && !isSingles ? this.#initializeColorDraft(colorsGroup) : null
      if (isSingles) this.#colorDraft = undefined
      const activePreset = colorData?.presets[colorData.checkedIndex]
      const panel = this.#createPanel({
        key: 'pack',
        label: this.#getPackLabel(packGroup),
        value: isSingles ? colorsGroup?.value || '' : this.#getPackName(activePreset, colorsGroup),
        groupName:
          packGroup?.source.querySelector('input[type="radio"]')?.name ||
          colorsGroup?.source.querySelector('input[type="radio"]')?.name ||
          'pack',
        open: this.#openPanelKey === 'pack',
        image: isSingles ? '' : activePreset?.image || '',
      })
      panel.classList.add('pdp-option-modals__panel--pack')
      const content = panel.querySelector('[data-option-panel-content]')

      if (packGroup) this.#appendPackQuantity(content, packGroup, index)
      if (colorsGroup && isSingles) {
        this.#appendSingleColors(content, colorsGroup, index)
      } else if (colorsGroup && colorData) {
        this.#appendPackColors(content, colorsGroup, colorData)
      }
      this.cards.append(panel)
    }

    #isSinglesPack(packGroup) {
      const selectedSize =
        packGroup?.source.querySelector('input[type="radio"]:checked')?.dataset.size ||
        this.productInfo.dataset.productPackSize ||
        ''
      return selectedSize.toLowerCase() === 'single'
    }

    #getPackLabel(packGroup) {
      const checked = packGroup?.source.querySelector('input[type="radio"]:checked')
      const size = checked?.dataset.size || this.productInfo.dataset.productPackSize || ''
      if (!size) return packGroup?.label || ''
      if (size.toLowerCase() === 'single') return this.dataset.singlesLabel
      return size.toLowerCase().includes('pack') ? size : `${size}-Pack`
    }

    #getPackName(preset, colorsGroup) {
      if (this.#colorDraft?.mode === 'custom' && !this.#colorDraft.items.length) {
        return this.dataset.customPackLabel
      }
      const selectedItems = this.#colorDraft?.mode === 'custom'
        ? this.#colorDraft.items
        : preset?.items
      const selectedColors = selectedItems?.map((item) => item.color).filter(Boolean) || []
      const itemSummary = this.#getColorSummary(selectedColors.join(','))
      if (itemSummary) return itemSummary

      const inputSummary = this.#getColorSummary(
        preset?.input?.dataset.colorList,
        preset?.input?.dataset.colorPack,
      )
      return inputSummary || colorsGroup?.value || ''
    }

    #appendPackQuantity(content, group, index) {
      const heading = document.createElement('p')
      heading.className = 'pdp-option-modals__subheading'
      heading.innerHTML = '<span></span><strong></strong>'
      heading.querySelector('span').textContent = this.dataset.quantityLabel
      const selectedSize =
        group.source.querySelector('input[type="radio"]:checked')?.dataset.size || group.value
      heading.querySelector('strong').textContent =
        Number.parseInt(selectedSize, 10) || (selectedSize.toLowerCase() === 'single' ? 1 : selectedSize)

      const options = group.source.querySelector('.liquid-pdp__pack-selector-buttons')
      if (!options) return
      const clonedOptions = this.#cloneSource({ ...group, source: options }, index)
      clonedOptions.classList.add('pdp-option-modals__pack-options')
      content.append(heading, clonedOptions)
    }

    #appendSingleColors(content, group, index) {
      const heading = document.createElement('p')
      heading.className = 'pdp-option-modals__subheading pdp-option-modals__subheading--colors'
      heading.innerHTML = '<span></span><strong></strong>'
      heading.querySelector('span').textContent = this.dataset.colorsLabel
      heading.querySelector('strong').textContent = group.value

      const swatches = this.#cloneSource(group, index)
      swatches.classList.add('pdp-option-modals__single-colors')
      swatches
        .querySelectorAll(
          '.liquid-pdp__product-colors-heading, .liquid-pdp__product-colors-buttons, .liquid-pdp__oos-substitute-bubble',
        )
        .forEach((element) => element.remove())
      content.append(heading, swatches)
    }

    #appendPackColors(content, group, colorData) {
      const activePreset = colorData.presets[colorData.checkedIndex]
      const originalColorList = group.source.querySelector('[data-color-display]')?.dataset.colorList
      const colors =
        this.#colorDraft.mode === 'custom'
          ? this.#colorDraft.items.map((item) => item.color).filter(Boolean)
          : originalColorList
            ? this.#getColors(originalColorList)
            : this.#getColors(
                activePreset?.input.dataset.colorList,
                activePreset?.input.dataset.colorPack,
              )
      const { shortList, fullList, remainingCount } =
        this.#getPackColorPresentation(colors)
      const heading = document.createElement('p')
      heading.className = 'pdp-option-modals__subheading pdp-option-modals__subheading--colors'
      heading.innerHTML = '<span></span><strong></strong><button type="button"></button>'
      heading.querySelector('span').textContent = this.dataset.packColorsLabel
      const colorDisplay = heading.querySelector('strong')
      const colorDisplayId = `PdpPackColors-${this.dataset.sectionId}`
      colorDisplay.id = colorDisplayId
      colorDisplay.textContent = shortList
      colorDisplay.setAttribute('aria-live', 'polite')
      colorDisplay.setAttribute('aria-atomic', 'true')
      const more = heading.querySelector('button')
      more.className = 'pdp-option-modals__colors-expand'
      more.dataset.packColorsExpand = ''
      more.dataset.allColors = fullList
      more.setAttribute('aria-controls', colorDisplayId)
      more.setAttribute('aria-expanded', 'false')
      more.textContent = this.dataset.moreLabel.replace('__COUNT__', remainingCount)
      more.hidden = remainingCount === 0

      const list = document.createElement('div')
      list.className = 'pdp-option-modals__pack-presets'
      const customPackSource = group.source.querySelector('[data-tcp-byo]')
      const canCustomize = Boolean(customPackSource && colorData.catalog.length)
      const visiblePresetLimit = canCustomize ? 3 : 4
      const sourceList = group.source.querySelector('.liquid-pdp__product-colors-buttons')
      const showMoreSource = group.source.querySelector('.curated-packs-show-more-button--tile')
      const shouldCollapsePresets = Boolean(
        showMoreSource &&
        !sourceList?.classList.contains('colors-buttons-expanded') &&
        colorData.checkedIndex < visiblePresetLimit,
      )
      if (sourceList?.classList.contains('colors-buttons-expanded')) {
        list.classList.add('pdp-option-modals__pack-presets--expanded')
      }
      const visiblePresets = shouldCollapsePresets
        ? colorData.presets.slice(0, visiblePresetLimit)
        : colorData.presets

      for (const [presetIndex, preset] of visiblePresets.entries()) {
        const activeIndex = this.#colorDraft.mode === 'custom' ? -1 : colorData.checkedIndex
        list.append(
          this.#createInlinePresetButton(preset, presetIndex, activeIndex, canCustomize),
        )
        if (presetIndex === 0 && canCustomize) {
          list.append(this.#createInlineCustomPackButton(customPackSource))
        }
      }

      if (shouldCollapsePresets) {
        const showMore = document.createElement('button')
        showMore.type = 'button'
        showMore.className =
          'pdp-option-modals__pack-show-more curated-packs-show-more-button--tile'
        showMore.dataset.packPresetsExpand = visiblePresetLimit
        showMore.setAttribute(
          'aria-label',
          showMoreSource.getAttribute('aria-label') || 'Show more packs',
        )
        showMore.setAttribute('aria-expanded', 'false')
        const showMoreIcon = document.createElement('div')
        showMoreIcon.setAttribute('aria-hidden', 'true')
        showMoreIcon.textContent = '+'
        showMore.append(showMoreIcon)
        list.append(showMore)
      }
      content.append(heading, list)
    }

    #createInlinePresetButton(preset, index, checkedIndex, canCustomize) {
      const card = document.createElement('div')
      card.className = 'pdp-option-modals__pack-preset'
      if (index === checkedIndex) card.classList.add('is-active')

      const select = document.createElement('button')
      select.type = 'button'
      select.className = 'pdp-option-modals__pack-preset-select'
      select.dataset.inlineColorPreset = index
      select.disabled = !this.#isColorAvailable({ input: preset.input })
      select.setAttribute('aria-label', preset.label)
      if (index === checkedIndex) select.setAttribute('aria-current', 'true')
      const action =
        preset.input
          .closest('.liquid-pdp__product-colors-button-wrapper')
          ?.querySelector('.liquid-pdp__color-button-edit span:last-child')
          ?.textContent.trim() || preset.label

      const media = document.createElement('span')
      media.className = 'pdp-option-modals__pack-preset-media'
      if (preset.image) {
        media.append(this.#createTileImage(preset.image, 120, 120))
      }
      select.append(media)

      const footer = document.createElement('button')
      footer.type = 'button'
      footer.className = 'pdp-option-modals__pack-preset-action'
      if (canCustomize) {
        footer.dataset.inlineColorCustomize = index
      } else {
        footer.dataset.inlineColorPreset = index
      }
      footer.setAttribute('aria-label', `${action}: ${preset.label}`)
      footer.textContent = action
      card.append(select, footer)

      // Hangs off the card rather than the image wrapper: the wrapper has to clip
      // its image, and clipping is what cut the badge in half.
      if (preset.badge) {
        const badge = document.createElement('span')
        badge.className = 'pdp-option-modals__pack-preset-badge'
        badge.textContent = preset.badge
        card.append(badge)
      }
      return card
    }

    #createInlineCustomPackButton(source) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'pdp-option-modals__pack-preset pdp-option-modals__pack-preset--custom'
      button.dataset.originalCustomPackEmpty = ''
      button.setAttribute('aria-label', this.dataset.customPackLabel)
      if (this.#colorDraft.mode === 'custom') {
        button.classList.add('is-active')
        button.setAttribute('aria-current', 'true')
      }

      const media = document.createElement('span')
      media.className = 'pdp-option-modals__pack-preset-media'
      const sourceImage = source.querySelector('img')
      if (sourceImage) {
        media.append(this.#createTileImage(sourceImage.currentSrc || sourceImage.src, 120, 120))
      }

      const footer = document.createElement('span')
      footer.className = 'pdp-option-modals__pack-preset-action'
      footer.textContent =
        source.querySelector('.liquid-pdp__color-button-edit span:last-child')?.textContent.trim() ||
        this.dataset.customPackLabel
      button.append(media, footer)
      return button
    }

    #renderGroup(group, index) {
      const dialogId = `PdpOptionDialog-${this.dataset.sectionId}-${index}`
      const titleId = `${dialogId}-Title`
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'pdp-option-modals__card'
      button.classList.add(`pdp-option-modals__card--${group.type}`)
      button.dataset.optionDialogTrigger = dialogId
      button.dataset.optionGroupName =
        group.source.querySelector('input[type="radio"]')?.name || group.label
      button.setAttribute('aria-haspopup', 'dialog')
      button.setAttribute('aria-controls', dialogId)
      button.innerHTML = `
        <span class="pdp-option-modals__card-copy">
          <span class="pdp-option-modals__card-label"></span>
          <strong class="pdp-option-modals__card-value"></strong>
        </span>
        <span class="pdp-option-modals__card-edit"></span>
      `
      button.querySelector('.pdp-option-modals__card-label').textContent = group.label
      button.querySelector('.pdp-option-modals__card-value').textContent = group.value
      button.querySelector('.pdp-option-modals__card-edit').textContent = this.dataset.editLabel
      if (group.type === 'colors') this.#renderColorSwatches(button, group.source)
      this.cards.append(button)

      const dialog = document.createElement('dialog')
      dialog.id = dialogId
      dialog.className = 'pdp-option-modals__dialog'
      dialog.dataset.sourceType = group.type
      dialog.setAttribute('aria-labelledby', titleId)
      dialog.setAttribute('aria-modal', 'true')

      dialog.innerHTML = `
        <div class="pdp-option-modals__dialog-header">
          <h2 class="pdp-option-modals__dialog-title" id="${titleId}"></h2>
          <button type="button" class="pdp-option-modals__close" data-option-dialog-close></button>
        </div>
        <div class="pdp-option-modals__dialog-body"></div>
      `
      dialog.querySelector('.pdp-option-modals__dialog-title').textContent = group.label
      const close = dialog.querySelector('.pdp-option-modals__close')
      close.setAttribute('aria-label', this.dataset.closeLabel)
      close.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"/></svg>'
      const body = dialog.querySelector('.pdp-option-modals__dialog-body')
      if (group.type === 'colors') {
        dialog.classList.add('pdp-option-modals__dialog--colors')
        this.#renderColorsDialog(group, body)
      } else if (group.type === 'pack') {
        dialog.classList.add('pdp-option-modals__dialog--pack')
        this.#renderPackDialog(group, body, index)
      } else {
        body.append(this.#cloneSource(group, index))
      }
      dialog.addEventListener('close', () => this.#activeTrigger?.focus())
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close()
      })
      this.append(dialog)
    }

    #renderPackDialog(group, body, index) {
      const options = group.source.querySelector('.liquid-pdp__pack-selector-buttons')
      if (!options) return

      const clonedOptions = this.#cloneSource(
        { ...group, source: options },
        index,
      )
      clonedOptions.classList.add('pdp-option-modals__pack-options')
      body.append(clonedOptions)
    }

    #renderColorsDialog(group, body) {
      const { presets, catalog } = this.#initializeColorDraft(group)

      const description = document.createElement('p')
      description.className = 'pdp-option-modals__color-description'
      description.textContent = this.dataset.colorsDescription

      const presetList = document.createElement('div')
      presetList.className = 'pdp-option-modals__preset-list'
      presets.forEach((preset, index) => presetList.append(this.#createPresetButton(preset, index)))

      if (group.source.querySelector('[data-tcp-byo]') && catalog.length) {
        const custom = document.createElement('button')
        custom.type = 'button'
        custom.className = 'pdp-option-modals__preset pdp-option-modals__preset--custom'
        custom.dataset.colorCustom = ''
        custom.innerHTML = `
          <span class="pdp-option-modals__preset-thumb pdp-option-modals__preset-thumb--custom">
            <span class="pdp-option-modals__preset-plus" aria-hidden="true">+</span>
          </span>
          <strong class="pdp-option-modals__preset-name"></strong>
        `
        custom.querySelector('.pdp-option-modals__preset-name').textContent =
          this.dataset.customPackLabel
        if (this.#colorDraft.mode === 'custom') {
          custom.classList.add('is-active')
          custom.setAttribute('aria-current', 'true')
        }
        presetList.append(custom)
      }

      body.append(description, presetList)
      this.#appendBundleEditor(body)
    }

    #appendBundleEditor(body) {
      const bundle = document.createElement('section')
      bundle.className = 'pdp-option-modals__bundle'
      bundle.innerHTML = `
        <div class="pdp-option-modals__bundle-header">
          <p><strong data-bundle-title></strong><span aria-hidden="true"> | </span><span data-bundle-help></span></p>
          <strong data-bundle-count></strong>
        </div>
        <div class="pdp-option-modals__bundle-grid" data-bundle-grid></div>
      `
      bundle.querySelector('[data-bundle-title]').textContent = this.dataset.yourBundleLabel
      bundle.querySelector('[data-bundle-help]').textContent = this.dataset.swapColorLabel

      const save = document.createElement('button')
      save.type = 'button'
      save.className = 'pdp-option-modals__save'
      save.dataset.colorSave = ''
      save.textContent = this.dataset.saveUpdateLabel.replaceAll('&amp;', '&')

      body.append(bundle, save)
      this.#renderBundleDraft(body)
    }

    #initializeColorDraft(group) {
      const catalog = this.#getColorCatalog()
      const presets = Array.from(group.source.querySelectorAll('input[type="radio"]')).map(
        (input, index) => this.#createPreset(input, catalog, index),
      )
      const checkedIndex = Math.max(
        0,
        presets.findIndex((preset) => preset.input.checked),
      )
      const activeSelections = this.productInfo.bundleSelections
      const isCustom =
        this.productInfo.dataset.bundleMode === 'true' &&
        Array.isArray(activeSelections)

      this.#colorDraft = {
        mode: isCustom ? 'custom' : 'preset',
        presetIndex: checkedIndex,
        items: isCustom
          ? activeSelections.map((selection) =>
              catalog.find((item) => item.handle === selection.handle) || {
                color: selection.color,
                handle: selection.handle,
                image: '',
                swatch: '',
              },
            )
          : presets[checkedIndex]?.items || [],
        presets,
        catalog,
      }
      return { presets, catalog, checkedIndex }
    }

    #createPreset(input, catalog, index) {
      const wrapper = input.closest('.liquid-pdp__product-colors-button-wrapper')
      const badge = wrapper?.querySelector('.badge')?.textContent.replace(/^[^a-zA-Z0-9]+/, '').trim()
      const colors = this.#expandPackColors(input.dataset.colorList, catalog)
      const image = wrapper?.querySelector('img')?.currentSrc || wrapper?.querySelector('img')?.src || ''
      return {
        input,
        badge,
        image,
        label: badge || this.#getPresetLabel(input, colors, index),
        items: colors,
      }
    }

    #getPresetLabel(input, colors, index) {
      const unique = [...new Set(colors.map((item) => item.color))]
      if (unique.length === 1) return `All ${unique[0]}`
      if (unique.length === 2) return unique.join(' & ')
      return input.getAttribute('aria-label') || input.value || `Pack ${index + 1}`
    }

    #createPresetButton(preset, index) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'pdp-option-modals__preset'
      button.dataset.colorPreset = index
      if (this.#colorDraft.mode === 'preset' && this.#colorDraft.presetIndex === index) {
        button.classList.add('is-active')
        button.setAttribute('aria-current', 'true')
      }

      const thumb = document.createElement('span')
      thumb.className = 'pdp-option-modals__preset-thumb'
      if (preset.badge) {
        const badge = document.createElement('span')
        badge.className = 'pdp-option-modals__preset-badge'
        badge.textContent = preset.badge
        thumb.append(badge)
      }
      if (preset.image) {
        thumb.append(
          this.#createTileImage(preset.image, 96, 96, 'pdp-option-modals__preset-image'),
        )
      }

      const label = document.createElement('strong')
      label.className = 'pdp-option-modals__preset-name'
      label.textContent = preset.label
      button.append(thumb, label)
      return button
    }

    #getColorCatalog() {
      const context = this.productInfo.querySelector('.js-pdp-product-context-json')
      let singles = this.productInfo.jsonData?.singles_data_json || []
      if (!singles.length && context) {
        try {
          singles = JSON.parse(context.textContent).singles_data_json || []
        } catch (_error) {
          singles = []
        }
      }

      let inputs = Array.from(
        this.productInfo.querySelectorAll('t-custom-pack product-colors input[data-color-list]'),
      )
      if (!inputs.length) {
        inputs = Array.from(
          this.productInfo.querySelectorAll('product-colors input[data-color-list]'),
        ).filter((input) => !this.contains(input))
      }
      return inputs.map((input) => {
        const single = singles.find((product) => product.handle === input.value)
        const image = single?.media?.[0]?.src || ''
        const swatch = input
          .closest('.liquid-pdp__product-color-swatch')
          ?.querySelector('.color')?.style.background
        return {
          color: input.dataset.colorList.trim(),
          handle: input.value,
          image: image.startsWith('//') ? `https:${image}` : image,
          input,
          swatch: swatch || '',
        }
      })
    }

    #isColorAvailable(item) {
      const input = item?.input
      if (!input) return false
      return (
        !input.disabled &&
        input.getAttribute('aria-disabled') !== 'true' &&
        input.dataset.disabled !== 'true' &&
        !input.classList.contains('disabled')
      )
    }

    #expandPackColors(raw, catalog) {
      const limit = Number(this.productInfo.dataset.bundleMaxItems) ||
        Number.parseInt(this.productInfo.dataset.productPackSize, 10) || 1
      const parsed = typeof this.productInfo.parsePackColorList === 'function'
        ? this.productInfo.parsePackColorList(raw || '', limit)
        : (raw || '').split(',').map((color) => ({ color: color.trim(), count: 1 }))
      const items = []
      parsed.forEach(({ color, count }) => {
        const item = catalog.find(
          (candidate) => candidate.color.toLowerCase() === color.toLowerCase(),
        )
        if (!item) return
        for (let index = 0; index < count; index += 1) items.push({ ...item })
      })
      if (items.length && items.length < limit) {
        const seed = [...items]
        while (items.length < limit) items.push({ ...seed[items.length % seed.length] })
      }
      return items.slice(0, limit)
    }

    #renderBundleDraft(scope) {
      const grid = scope.querySelector('[data-bundle-grid]')
      const count = scope.querySelector('[data-bundle-count]')
      if (!grid || !count || !this.#colorDraft) return
      grid.replaceChildren()
      const limit = Number(this.productInfo.dataset.bundleMaxItems) || this.#colorDraft.items.length
      count.textContent = `${this.#colorDraft.items.length} / ${limit}`

      this.#colorDraft.items.forEach((item, index) => {
        const tile = document.createElement('article')
        tile.className = 'pdp-option-modals__bundle-tile'
        if (item.image) {
          tile.append(
            this.#createTileImage(item.image, 180, 220, 'pdp-option-modals__bundle-image'),
          )
        }
        const footer = document.createElement('div')
        footer.className = 'pdp-option-modals__bundle-tile-footer'
        const name = document.createElement('strong')
        name.textContent = item.color
        const edit = document.createElement('button')
        edit.type = 'button'
        edit.className = 'pdp-option-modals__bundle-edit'
        edit.dataset.bundleSlotEdit = index
        edit.setAttribute('aria-label', `${this.dataset.editColorLabel}: ${item.color}`)
        edit.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m4 20 4.3-1 10.5-10.5-3.3-3.3L5 15.7 4 20Zm10-13.3 3.3 3.3" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="1.8"/></svg>'
        footer.append(name, edit)
        tile.append(footer)
        grid.append(tile)
      })
    }

    #buildBundleSelections(items) {
      const main = this.productInfo.getMainVariantSelects?.()
      const selectedSize = main
        ? Array.from(main.querySelectorAll('input:checked')).map((input) => input.value).join(' / ')
        : ''
      const singles = this.productInfo.jsonData?.singles_data_json || []
      return items.map((item) => {
        const single = singles.find((product) => product.handle === item.handle)
        const targetSize = item.variantTitle || selectedSize
        const variant = targetSize
          ? single?.variants?.find((candidate) => candidate.title === targetSize)
          : null
        return {
          id: variant?.id || item.variantId || null,
          title: variant?.title || item.variantTitle || '',
          color: item.color,
          handle: item.handle,
        }
      })
    }

    #syncDraftToOriginalBuilder() {
      if (!this.productInfo.isCustomPackEligible?.() || !this.#colorDraft?.items.length) return null
      const tcp = this.productInfo.querySelector('t-custom-pack')
      if (!tcp) return null

      this.productInfo.bundleSelections = this.#buildBundleSelections(this.#colorDraft.items)
      tcp.slotStyles = this.#colorDraft.items.map((item) => item.swatch || '')
      tcp.renderSlots?.()
      return tcp
    }

    #cloneSource(group, index) {
      const clone = document.createElement('div')
      clone.className = `${group.source.className || ''} pdp-option-modals__source-clone`.trim()
      clone.dataset.sourceType = group.type
      clone.dataset.optionName = group.source.dataset.optionName || ''
      clone.innerHTML = group.source.innerHTML

      const sourceInputs = group.source.querySelectorAll('input[type="radio"]')
      const cloneInputs = clone.querySelectorAll('input[type="radio"]')
      for (const [inputIndex, cloneInput] of cloneInputs.entries()) {
        const sourceInput = sourceInputs[inputIndex]
        if (!sourceInput) continue
        cloneInput.checked = sourceInput.checked
        cloneInput.defaultChecked = sourceInput.defaultChecked
      }

      this.#sanitizeClone(clone, index)
      return clone
    }

    #handleSizeChanged = () => {
      this.#syncVariantGroups()
    }

    #syncVariantGroups() {
      const sources = Array.from(
        this.productInfo.querySelectorAll('.liquid-pdp__variant-selector-buttons'),
      ).filter(
        (source) =>
          !this.contains(source) &&
          !source.closest('t-custom-pack') &&
          source.closest('variant-selects')?.style.display !== 'none',
      )
      const clones = this.querySelectorAll(
        '.pdp-option-modals__source-clone[data-source-type="variant"]',
      )

      for (const clone of clones) {
        const source = sources.find(
          (candidate) => candidate.dataset.optionName === clone.dataset.optionName,
        )
        if (!source) continue

        const originalInputs = source.querySelectorAll('input[type="radio"]')
        const cloneInputs = clone.querySelectorAll('input[type="radio"]')
        for (const cloneInput of cloneInputs) {
          const original = Array.from(originalInputs).find(
            (candidate) =>
              candidate.name === cloneInput.dataset.originalName &&
              candidate.value === cloneInput.value,
          )
          cloneInput.checked = Boolean(original?.checked)
        }

        const selected = Array.from(cloneInputs).find((input) => input.checked)
        const value =
          selected
            ?.closest('label')
            ?.querySelector('.liquid-pdp__variant-selector-button-text')
            ?.textContent.trim() || selected?.value || ''
        const panel = clone.closest('[data-option-panel]')
        const display = panel?.querySelector('.pdp-option-modals__panel-value')
        if (display) display.textContent = value
        if (selected) this.#closePanel(panel)
      }
    }

    #renderColorSwatches(button, source) {
      const checked = source.querySelector('input[type="radio"]:checked')
      const display = source.querySelector('[data-color-display]')
      const colors = this.#getColors(
        checked?.dataset.colorList,
        checked?.dataset.colorPack,
        display?.dataset.colorList,
        display?.textContent,
      ).slice(0, 4)
      if (!colors.length) return

      const swatches = document.createElement('span')
      swatches.className = 'pdp-option-modals__swatches'
      swatches.setAttribute('aria-hidden', 'true')
      for (const color of colors) {
        const swatch = document.createElement('span')
        const colorHandle = this.#getColorHandle(color)
        swatch.className = 'pdp-option-modals__swatch'
        swatch.title = color
        swatch.style.setProperty(
          '--pdp-option-swatch-background',
          colorHandle
            ? `var(--swatch-${colorHandle}, var(--brand-gray-light))`
            : 'var(--brand-gray-light)',
        )
        swatches.append(swatch)
      }
      button.prepend(swatches)
      button.classList.add('pdp-option-modals__card--colors')
    }

    #getColorHandle(color) {
      return color
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[’']/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
    }

    #sanitizeClone(clone, index) {
      const animatedElements = [
        clone,
        ...clone.querySelectorAll('.anim-slide-up, [data-animate], [data-animate-init]'),
      ]
      const animationAttributes = [
        'data-animate',
        'data-animate-class',
        'data-animate-delay',
        'data-animate-init',
        'data-animate-once',
        'data-animate-stagger',
        'data-animate-stagger-offset',
        'data-animate-threshold',
      ]

      for (const element of new Set(animatedElements)) {
        element.classList.remove('anim-slide-up', 'is-visible')
        for (const attribute of animationAttributes) element.removeAttribute(attribute)

        // Dynamic PDP swaps may leave the fade helper's inline animation state
        // on the source. Modal clones render immediately and own no scroll state.
        element.style.removeProperty('opacity')
        element.style.removeProperty('transform')
        element.style.removeProperty('transition')
      }

      const idMap = new Map()
      clone.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id
        const newId = `${oldId}-poc-${index}`
        idMap.set(oldId, newId)
        element.id = newId
      })

      for (const attribute of ['for', 'aria-labelledby', 'aria-controls', 'aria-describedby']) {
        clone.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const references = element
            .getAttribute(attribute)
            .split(' ')
            .map((reference) => idMap.get(reference) || reference)
          element.setAttribute(attribute, references.join(' '))
        })
      }

      clone.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.dataset.originalName = input.name
        input.dataset.optionGroupName = input.name
        input.name = `poc-${index}-${input.name}`
        // Variant options follow the original template, which marks an unavailable
        // size aria-disabled but never truly disabled: it stays selectable so the
        // sold-out state can surface. Only the other option types hard-disable.
        if (clone.dataset.sourceType === 'variant') return
        if (input.getAttribute('aria-disabled') === 'true' || input.classList.contains('disabled')) {
          input.disabled = true
        }
      })

      // Every rebuild inserts fresh <img> elements. Cloned markup carries the
      // source's loading="lazy", and a newly inserted lazy image is not fetched or
      // decoded until the browser gets round to it, so tiles that were on screen a
      // moment ago blank to grey and fill back in - a visible flicker even when the
      // bytes are already cached. These are all above the fold by definition.
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager'
        image.decoding = 'sync'
        image.removeAttribute('data-src')
      })

      clone.querySelectorAll('script, template').forEach((element) => element.remove())
    }

    #openColorBuilder(trigger, preset = null) {
      const builderId =
        trigger.dataset.colorBuilderTrigger || `PdpColorBuilder-${this.dataset.sectionId}`
      const builder = this.querySelector(`#${CSS.escape(builderId)}`)
      if (!builder || !this.#colorDraft) return

      if (!builder.hidden && this.#activeTrigger === trigger) {
        builder.hidden = true
        trigger.setAttribute('aria-expanded', 'false')
        this.#activeTrigger = null
        return
      }

      if (preset || trigger.hasAttribute('data-color-builder-custom')) {
        const limit = Number(this.productInfo.dataset.bundleMaxItems) || 1
        const seed = preset?.items.length
          ? preset.items
          : this.#colorDraft.items.length
            ? this.#colorDraft.items
            : this.#colorDraft.catalog.slice(0, 1)
        if (!seed.length) return
        this.#colorDraft.mode = 'custom'
        this.#colorDraft.presetIndex = -1
        this.#colorDraft.items = Array.from({ length: limit }, (_, index) => ({
          ...seed[index % seed.length],
        }))
        for (const button of builder.querySelectorAll('.pdp-option-modals__preset')) {
          const active = button.hasAttribute('data-color-custom')
          button.classList.toggle('is-active', active)
          if (active) button.setAttribute('aria-current', 'true')
          else button.removeAttribute('aria-current')
        }
        this.#renderBundleDraft(builder)
      }

      this.#activeTrigger?.setAttribute('aria-expanded', 'false')
      this.#activeTrigger = trigger
      trigger.setAttribute('aria-expanded', 'true')
      builder.hidden = false
    }

    #handleClick = (event) => {
      // Collapse the other panels here rather than in #handlePanelToggle. `toggle`
      // is dispatched asynchronously, so by the time that handler closed the
      // sibling the browser had already painted a frame with two panels expanded
      // - a full-height jump on every Edit. This runs before <details> applies
      // its own default action, so both changes land in the same frame.
      const summary = event.target.closest('.pdp-option-modals__summary')
      if (summary) {
        const panel = summary.closest('[data-option-panel]')
        for (const otherPanel of this.querySelectorAll('[data-option-panel][open]')) {
          if (otherPanel !== panel) this.#closePanel(otherPanel)
        }
        if (panel) {
          // <details> applies its own toggle after this handler returns, so mirror
          // the state it is about to land in. Leaving this to the async `toggle`
          // event painted the panel expanded while its action still read "Edit",
          // and armed the fade a frame after the content was already opaque.
          const willOpen = !panel.open
          const action = panel.querySelector('.pdp-option-modals__panel-action')
          if (action) {
            action.textContent = willOpen ? this.dataset.doneLabel : this.dataset.editLabel
          }
          // The one place the intended-open card is decided: opening a summary sets
          // it, and Done clears it. Everything else just renders that decision.
          this.#openPanelKey = willOpen ? panel.dataset.panelKey : undefined
          if (willOpen) this.#markPanelOpening(panel)
        }
      }

      const packPresetsExpand = event.target.closest('[data-pack-presets-expand]')
      if (packPresetsExpand && this.#colorDraft) {
        const list = packPresetsExpand.closest('.pdp-option-modals__pack-presets')
        const startIndex = Number(packPresetsExpand.dataset.packPresetsExpand)
        if (!list || !Number.isInteger(startIndex)) return

        const canCustomize = Boolean(
          this.productInfo.querySelector('product-colors [data-tcp-byo]') &&
          this.#colorDraft.catalog.length,
        )
        const activeIndex = this.#colorDraft.mode === 'custom'
          ? -1
          : this.#colorDraft.presetIndex
        const fragment = document.createDocumentFragment()
        for (const [offset, preset] of this.#colorDraft.presets.slice(startIndex).entries()) {
          fragment.append(
            this.#createInlinePresetButton(
              preset,
              startIndex + offset,
              activeIndex,
              canCustomize,
            ),
          )
        }

        const firstRevealed = fragment.querySelector('.pdp-option-modals__pack-preset-select')
        this.productInfo
          .querySelector('product-colors .curated-packs-show-more-button--tile')
          ?.click()
        list.classList.add('pdp-option-modals__pack-presets--expanded')
        packPresetsExpand.setAttribute('aria-expanded', 'true')
        packPresetsExpand.replaceWith(fragment)
        firstRevealed?.focus()
        return
      }

      const singleSwatchesToggle = event.target.closest(
        '.pdp-option-modals__single-colors .js-hidden-swatches-toggle',
      )
      if (singleSwatchesToggle) {
        const wrapper = singleSwatchesToggle.closest(
          '.liquid-pdp__product-color-swatches-wrapper',
        )
        if (!wrapper) return
        const isExpanded = wrapper.classList.toggle('showing-all-swatches')
        singleSwatchesToggle.textContent = isExpanded
          ? singleSwatchesToggle.dataset.showLessText
          : singleSwatchesToggle.dataset.showMoreText
        singleSwatchesToggle.setAttribute('aria-expanded', String(isExpanded))
        return
      }

      const colorsExpand = event.target.closest('[data-pack-colors-expand]')
      if (colorsExpand) {
        const displayId = colorsExpand.getAttribute('aria-controls')
        const display = displayId ? this.querySelector(`#${CSS.escape(displayId)}`) : null
        if (!display) return
        display.textContent = colorsExpand.dataset.allColors
        colorsExpand.setAttribute('aria-expanded', 'true')
        colorsExpand.hidden = true
        return
      }

      const inlineCustomize = event.target.closest('[data-inline-color-customize]')
      if (inlineCustomize && this.#colorDraft) {
        const preset = this.#colorDraft.presets[Number(inlineCustomize.dataset.inlineColorCustomize)]
        const rawColors = preset?.input?.dataset.colorList || preset?.input?.dataset.colorPack || ''
        if (!preset || !this.productInfo.isCustomPackEligible?.()) return
        this.#openPanelKey = 'pack'
        this.productInfo.revealTCustomPack?.(rawColors)
        this.#scheduleRebuild()
        return
      }

      const customPack = event.target.closest('[data-original-custom-pack-empty]')
      if (customPack) {
        if (!this.productInfo.isCustomPackEligible?.()) return
        this.#openPanelKey = 'pack'
        this.productInfo.revealTCustomPackEmpty?.()
        this.#scheduleRebuild()
        return
      }

      const inlinePreset = event.target.closest('[data-inline-color-preset]')
      if (inlinePreset && this.#colorDraft) {
        const preset = this.#colorDraft.presets[Number(inlinePreset.dataset.inlineColorPreset)]
        const original = preset?.input
        if (!original || !this.#isColorAvailable({ input: original })) return
        const presetPanel = inlinePreset.closest('[data-option-panel]')
        if (!this.#isPackPanel(presetPanel)) {
          this.#openPanelKey = undefined
          this.#closePanel(presetPanel, { focusSummary: true })
        }
        this.#startLoadingState()
        original.click()
        this.#scheduleRebuild()
        return
      }

      const trigger = event.target.closest('[data-option-dialog-trigger]')
      if (trigger) {
        const dialog = this.querySelector(`#${CSS.escape(trigger.dataset.optionDialogTrigger)}`)
        if (!dialog) return
        this.#activeTrigger = trigger
        dialog.showModal()
        const firstControl =
          dialog.querySelector('input:checked') ||
          dialog.querySelector('input:not([disabled])') ||
          dialog.querySelector('button')
        firstControl?.focus()
        return
      }

      const preset = event.target.closest('[data-color-preset]')
      if (preset && this.#colorDraft) {
        const index = Number(preset.dataset.colorPreset)
        const selected = this.#colorDraft.presets[index]
        if (!selected) return
        this.#colorDraft.mode = 'preset'
        this.#colorDraft.presetIndex = index
        this.#colorDraft.items = selected.items.map((item) => ({ ...item }))
        preset.closest('.pdp-option-modals__preset-list')?.querySelectorAll('.pdp-option-modals__preset').forEach((button) => {
          const active = button === preset
          button.classList.toggle('is-active', active)
          if (active) button.setAttribute('aria-current', 'true')
          else button.removeAttribute('aria-current')
        })
        this.#renderBundleDraft(
          preset.closest('[data-color-builder-content], .pdp-option-modals__dialog-body'),
        )
        return
      }

      const custom = event.target.closest('[data-color-custom]')
      if (custom && this.#colorDraft) {
        const limit = Number(this.productInfo.dataset.bundleMaxItems) || 1
        const seed = this.#colorDraft.items.length
          ? this.#colorDraft.items
          : this.#colorDraft.catalog.slice(0, 1)
        this.#colorDraft.mode = 'custom'
        this.#colorDraft.presetIndex = -1
        this.#colorDraft.items = Array.from({ length: limit }, (_, index) => ({
          ...seed[index % seed.length],
        }))
        custom.closest('.pdp-option-modals__preset-list')?.querySelectorAll('.pdp-option-modals__preset').forEach((button) => {
          const active = button === custom
          button.classList.toggle('is-active', active)
          if (active) button.setAttribute('aria-current', 'true')
          else button.removeAttribute('aria-current')
        })
        this.#renderBundleDraft(
          custom.closest('[data-color-builder-content], .pdp-option-modals__dialog-body'),
        )
        return
      }

      const save = event.target.closest('[data-color-save]')
      if (save && this.#colorDraft) {
        this.#saveColorDraft(save.closest('dialog'))
        return
      }

      const close = event.target.closest('[data-option-dialog-close]')
      if (close) {
        close.closest('dialog')?.close()
        return
      }

      const sizeGuide = event.target.closest('[data-size-guide-proxy]')
      if (sizeGuide) this.productInfo.querySelector('[data-pdp-options-source] open-size-chart')?.click()
    }

    #saveColorDraft(dialog) {
      if (this.#colorDraft.mode === 'preset') {
        const original = this.#colorDraft.presets[this.#colorDraft.presetIndex]?.input
        if (original && !original.disabled && original.getAttribute('aria-disabled') !== 'true') {
          this.#startLoadingState()
          original.click()
        }
        dialog?.close()
        this.#scheduleRebuild()
        return
      }

      const tcp = this.#syncDraftToOriginalBuilder()
      if (!tcp) return
      this.productInfo.enterBundleMode?.()
      dialog?.close()
      this.#scheduleRebuild()
    }

    #handleChange = (event) => {
      const input = event.target.closest('.pdp-option-modals__source-clone input[type="radio"]')
      if (!input) return

      const dialog = input.closest('dialog')
      const panel = input.closest('[data-option-panel]')
      const sourceType = input.closest('[data-source-type]')?.dataset.sourceType
      const original = this.#getOriginalInput(input, sourceType)

      if (!original || original.disabled) return
      // Unavailable sizes are selectable in the original template, so the
      // aria-disabled guard applies to every option type except variant.
      if (sourceType !== 'variant' && original.getAttribute('aria-disabled') === 'true') return
      if (sourceType !== 'variant') this.#startLoadingState()
      if (!this.#isPackPanel(panel)) this.#closePanel(panel, { focusSummary: true })
      original.click()
      if (sourceType === 'variant') {
        this.#syncVariantGroups()
        return
      }
      dialog?.close()
      this.#scheduleRebuild()
    }

    #getOriginalInput(input, sourceType) {
      const sourceSelector = this.#getSourceSelector(sourceType)
      if (!sourceSelector) return null

      const sources = Array.from(this.productInfo.querySelectorAll(sourceSelector)).filter(
        (source) =>
          !this.contains(source) &&
          (sourceType !== 'variant' ||
            (!source.closest('t-custom-pack') &&
              source.closest('variant-selects')?.style.display !== 'none')),
      )

      return (
        sources
          .flatMap((source) => Array.from(source.querySelectorAll('input[type="radio"]')))
          .find(
            (candidate) =>
              candidate.name === input.dataset.originalName && candidate.value === input.value,
          ) || null
      )
    }

    #getSourceSelector(type) {
      const selectors = {
        variant: '.liquid-pdp__variant-selector-buttons',
        style: '.js-your-cut-selector product-style',
        pack: 'product-pack-selector',
        colors: 'product-colors',
      }
      return selectors[type]
    }
  }

  customElements.define('pdp-option-modals', PdpOptionModals)
}
